import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, Loader } from 'lucide-react';
import html2canvas from 'html2canvas';

type ShareQuoteProps = {
  quote: {
    text: string;
    author: string;
    date: string;
    insight?: string;
  };
  isTranslated: boolean;
  translatedText: string;
  onClose: () => void;
};

const ShareQuote: React.FC<ShareQuoteProps> = ({ quote, isTranslated, translatedText, onClose }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleSave = async () => {
    setIsGenerating(true);
    const element = document.getElementById('quote-image');
    if (element) {
      try {
        const canvas = await html2canvas(element, {
          scale: 2,
          logging: false,
          useCORS: true
        });
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = 'daily-wisdom-quote.png';
        link.click();
      } catch (error) {
        console.error('Error generating image:', error);
        alert('Failed to generate image. Please try again.');
      }
    }
    setIsGenerating(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Share Quote</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        <div id="quote-image" className="bg-gradient-to-br from-indigo-100 to-purple-100 p-6 rounded-lg shadow-lg">
          <div className="text-sm text-indigo-600 font-semibold mb-2">{quote.date}</div>
          <blockquote className="text-xl italic mb-4 text-gray-700">
            "{isTranslated ? translatedText : quote.text}"
          </blockquote>
          <p className="text-right text-lg text-indigo-600 font-semibold">- {quote.author}</p>
          {isTranslated && (
            <p className="mt-2 text-sm text-gray-500">Original: "{quote.text}"</p>
          )}
          {quote.insight && (
            <p className="mt-4 text-sm text-gray-600">
              <span className="font-semibold">Insight:</span> {quote.insight}
            </p>
          )}
          <div className="mt-4 flex justify-between items-end">
            <span className="text-sm font-semibold text-gray-600">Daily Wisdom</span>
            <QRCodeSVG value="https://daily-wisdom.shenslu.com" size={64} />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isGenerating}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center transition duration-300 disabled:opacity-50"
          >
            {isGenerating ? (
              <>
                <Loader size={18} className="mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download size={18} className="mr-2" />
                Save Image
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareQuote;